import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authApi } from '@/lib/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const inviteCode = searchParams.get('invite');
  const orgSlug = searchParams.get('org');

  // Determine redirect URL based on context
  const getRedirectUrl = () => {
    if (inviteCode) return `/login?redirect=${encodeURIComponent(`/invite/${inviteCode}`)}`;
    if (orgSlug) return `/login?redirect=${encodeURIComponent(`/join/${orgSlug}`)}`;
    return '/login';
  };

  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  // Auto-redirect after email verification
  useEffect(() => {
    if (!isVerified) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(getRedirectUrl());
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isVerified, navigate, inviteCode, orgSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authApi.verifyEmail(email, code);

      if (response.data.success) {
        setIsVerified(true);
        toast.success('Email verified successfully!');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setIsResending(true);

    try {
      const response = await authApi.resendVerification(email);

      if (response.data.success) {
        toast.success('Verification code sent! Check your email.');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  if (isVerified) {
    const redirectUrl = getRedirectUrl();
    const isJoinFlow = inviteCode || orgSlug;

    return (
      <div className="animate-fade-in text-center">
        <div className="w-16 h-16 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-sage" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="font-serif text-3xl text-charcoal mb-2">Email verified!</h2>
        <p className="text-charcoal/60 mb-8">
          Your email has been verified successfully.<br />
          {isJoinFlow
            ? `Sign in to complete joining the program. Redirecting in ${countdown} second${countdown !== 1 ? 's' : ''}...`
            : `Redirecting in ${countdown} second${countdown !== 1 ? 's' : ''}...`
          }
        </p>
        <button
          onClick={() => navigate(redirectUrl)}
          className="btn btn-primary"
        >
          Sign in to continue
        </button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="lg:hidden mb-8 text-center">
        <Link to="/" className="font-display text-2xl font-bold text-charcoal tracking-tight">
          venndela
        </Link>
      </div>

      <h2 className="font-serif text-3xl text-charcoal mb-2">Verify your email</h2>
      <p className="text-charcoal/60 mb-8">
        We sent a verification code to your email address.
        Enter it below to verify your account.
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="label">Email address</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <label htmlFor="code" className="label">Verification code</label>
          <input
            id="code"
            type="text"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input text-center text-2xl tracking-widest"
            placeholder="000000"
            maxLength={6}
            required
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !code}
          className="btn btn-primary w-full py-3"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Verifying...
            </span>
          ) : (
            'Verify email'
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-charcoal/60 text-sm mb-2">Didn't receive the code?</p>
        <button
          type="button"
          onClick={handleResendCode}
          disabled={isResending}
          className="text-rust hover:text-rust-light font-medium text-sm"
        >
          {isResending ? 'Sending...' : 'Resend verification code'}
        </button>
      </div>

      <p className="mt-8 text-center text-sm text-charcoal/60">
        Already verified?{' '}
        <Link to="/login" className="text-rust hover:text-rust-light font-medium">
          Sign in
        </Link>
      </p>
    </div>
  );
}
